<template>
  <div class="w-full">
    <div class="min-w-36" :class="[containerClass]">
      <button
        ref="mainBtn"
        @click="toggleDropdown"
        @keydown="handleKeyDown"
        type="button"
        :title="selOptionDisplay"
        class="inline-flex items-center justify-center w-full border border-gray-300 dark:border-gray-800 px-4 py-2 text-sm font-medium text-black dark:text-gray-300 focus:bg-white-off dark:focus:bg-navy-300 focus:ring-2 focus:ring-primary relative"
        :class="dropdownClasses"
        :disabled="disabled"
      >
        <p v-if="!hasValue && placeholder" class="ml-auto truncate text-gray-500 dark:text-gray-400 font-medium dropdown-p-width">
          {{ placeholder }}
        </p>
        <div
          v-else
          class="ml-auto truncate dropdown-p-width flex items-center"
          :class="hasLeftAppends ? 'justify-start text-left' : 'justify-center text-center'"
        >
          <div
            v-if="hasLeftAppends"
            class="flex items-center justify-center shrink-0 w-7 mr-3"
          >
            <div
              v-for="(append, appendIdx) in visibleLeftAppends"
              :key="`sel-left-${appendIdx}`"
              :class="append.classes"
              class="flex items-center justify-center"
            >
              <font-awesome-icon
                v-if="append.icon"
                :icon="append.icon"
                :size="append.size"
              />
              <component
                v-else-if="append.component"
                :is="append.component"
                v-bind="append.props"
              />
            </div>
          </div>
          <p class="truncate min-w-0" :class="hasLeftAppends ? 'flex-1 text-left' : 'text-center'">
            <component
              v-for="part in selOptionHTML"
              :is="part.type ? part.type : 'span'"
              :key="part.key"
            >
              {{ part.content }}
            </component>
          </p>
          <div
            v-for="(append, appendIdx) in visibleRightAppends"
            :key="`sel-right-${appendIdx}`"
            :class="append.classes"
            class="mx-2 shrink-0"
          >
            <font-awesome-icon
              v-if="append.icon"
              :icon="append.icon"
              :size="append.size"
            />
            <component
              v-else-if="append.component"
              :is="append.component"
              v-bind="append.props"
            />
          </div>
        </div>
        <div
          class="flex items-center -mr-1 ml-auto"
          :class="
            isOpen
              ? 'transition-all transform rotate-180'
              : 'transition-all transform rotate-0'
          "
        >
          <font-awesome-icon icon="fa-solid fa-chevron-down" size="lg" class="h-4 w-4 text-gray-600" />
        </div>
      </button>
    </div>

    <VDropdownMenu
      ref="dropped"
      :options="filteredOptions"
      :sel-option="value"
      :is-open="isOpen"
      :passed-key-down="keyDown"
      :object-index="objectIndex"
      :fields="fields"
      :searchable="searchable"
      :search-placeholder="searchPlaceholder"
      @search="search"
      @select="select"
      @return-focus="returnFocus"
      @close-dropdown="closeDropdown"
    >
      <template #option="{ option }">
        <slot name="option" :option="option" />
      </template>
    </VDropdownMenu>
  </div>
</template>

<script>
import { mapState } from "vuex"
import VDropdownMenu from './VDropdownMenu.vue'
import PriorityChip from "@/components/Chips/PriorityChip.vue"
import { ChevronDownIcon } from "@heroicons/vue/24/outline"
import { normalizeDropdownAppends } from "./dropdownAppend.js"

export default {
  name: "VDropdownSelector",
  components: {
    VDropdownMenu,
    PriorityChip,
    ChevronDownIcon,
  },
  props: {
    options: Array,
    value: null,
    containerClass: String,
    required: {
      type: Boolean,
      default: false,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    objectIndex: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      required: false
    },
    fields: {
      type: Object,
      default: () => ({ text: "name", value: "id" }),
    },
    searchable: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      required: false
    },
    height: {
      type: String,
      default: "md"
    },
    hasAttachedBtn: {
      type: Boolean,
      default: false
    }
  },
  emits: ["select"],
  data() {
    return {
      isOpen: false,
      isFocused: false,
      keyDown: "",
      filteredOptions: [],
    }
  },
  computed: {
    ...mapState(["screen"]),
    selOptionDisplay() {
      if (
        typeof this.value === "string" ||
        typeof this.value === "undefined"
      )
        return this.value
      else if (typeof this.value === "object" && this.value !== null) {
        // retrieve text value if only given id
        let textVal = this.value[this.fields.text]
          ? this.value[this.fields.text]
          : this.options.find(x => x[this.fields.value] === this.value[this.fields.value])?.[this.fields.text]
        if (!textVal) return ""
        textVal = textVal.replace('_', ' ')
        return textVal
      }
      else return null
    },
    selOptionHTML() {
      if (!this.selOptionDisplay) return ""
      return this.selOptionDisplay.split(/(\^\w+)/g).map((part, index) => {
        if (part.startsWith('^')) {
          return { type: 'sup', content: part.slice(1), key: `sup-${index}` }
        }
        return { type: 'text', content: part, key: `text-${index}` }
      })
    },
    selOptionObj() {
      if (
        typeof this.value === "string" ||
        typeof this.value === "undefined"
      )
        return null
      if (!this.value) return null
      return this.options.find(x => x[this.fields.value] === this.value[this.fields.value])
    },
    visibleLeftAppends() {
      return normalizeDropdownAppends(this.selOptionObj?.dropdownAppend).left
        .filter(append => !append.showOnlyInDropdown)
    },
    visibleRightAppends() {
      return normalizeDropdownAppends(this.selOptionObj?.dropdownAppend).right
        .filter(append => !append.showOnlyInDropdown)
    },
    hasLeftAppends() {
      return this.visibleLeftAppends.length > 0
    },
    dropdownClasses() {
      let classes = this.height
      classes += this.disabled
        ? " bg-gray-100 hover:bg-gray-100 dark:bg-gray-500 dark:hover:bg-gray-500 cursor-not-allowed "
        : " bg-white dark:bg-input-dark hover:bg-white-off dark:hover:bg-input-darkhovered"
      classes += this.hasAttachedBtn
        ? " rounded-l"
        : " rounded"
      return classes
    },
    hasValue() {
      if (typeof(this.value) === "string") return this.value
      return this.value && this.value?.[this.fields.value] !== null && this.value?.[this.fields.value] !== undefined
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
  },
  watch: {
    options: {
      handler(val, oldVal) {
        if (val !== oldVal) this.sortOptions()
      },
      deep: true
    }
  },
  mounted() {
    this.filteredOptions = this.options
    document.addEventListener("click", this.handleDocumentClick, true)
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick, true)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
      if (this.searchable) {
        if (!this.isMobile) this.focusOnSearch()
        if (this.isMobile && !this.screen.md && this.isOpen) {
          this.$refs.mainBtn.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }
      else this.returnFocus()
    },
    async handleDocumentClick(e) {
      const clickedOnDropdownBtn = e.target === this.$refs.mainBtn || this.$refs.mainBtn.contains(e.target)
      const clickedInsideDropdown = this.$refs.dropped.$el.contains(e.target)
      if (!clickedOnDropdownBtn && !clickedInsideDropdown) {
        await this.$nextTick()
        if (this.isOpen) this.isOpen = false
      }
    },
    closeDropdown() {
      this.isOpen = false
    },
    async select(option) {
      this.$emit("select", { itemData: option })
      this.isOpen = false
      this.returnFocus()
      await this.$nextTick()
      this.sortOptions()
    },
    assignKey(val) {
      this.keyDown = val
    },
    async handleKeyDown(e) {
      // close dropdown if hitting Tab
      if (e.key === "Tab") {
        this.isOpen = false
      }
      // allow user to select option using keyboard
      // if presses down arrow or letter
      if (e.keyCode === 40 || (e.keyCode > 64 && e.keyCode < 91)) {
        console.log("DropdownSelector down")
        // e.preventDefault()
        await this.assignKey(e.key)
        this.$refs.dropped.$el.focus()
      }
    },
    async focusOnSearch() {
      await this.$nextTick()
      this.$refs.dropped?.$refs.searchInput.focus()
      setTimeout(() => {
        this.$refs.dropped?.$el.focus()
        this.$refs.dropped?.$refs.searchInput.focus()
      }, 75)
    },
    async returnFocus() {
      this.$refs.mainBtn.focus()
    },
    search(input) {
      if (!input) input = ""
      let arr = []
      if (typeof(this.options)[0] === "string") {
        for (let option of this.options) {
          // skip if not included in search
          if (!option.toLowerCase().includes((input.toLowerCase()))) continue
          // place first if is selected value
          if (this.value && option === this.value) arr.unshift(option)
          // else add to filtered options
          else arr.push(option)
        }
      }
      else if (typeof(this.options)[0] === "object") {
        for (let option of this.options) {
          // skip if not included in search
          if (!option[this.fields.text].toLowerCase().includes(input.toLowerCase())) continue
          // place first if is selected value
          if (this.value && option[this.fields.value] === this.value[this.fields.value]) arr.unshift(option)
          // else add to filtered options
          else arr.push(option)
        }
      }
      this.filteredOptions = arr
    },
    sortOptions() {
      // place selected option first
      if (this.value) {
        let arr = []
        if (typeof this.value === "string") {
          arr = [ this.value, ...this.options.filter(x => x !== this.value) ]
        }
        else arr = [ this.selOptionObj, ...this.options.filter(x => x[this.fields.value] !== this.value[this.fields.value]) ]
        this.filteredOptions = arr
      }
      else this.filteredOptions = this.options
    }
  }
};
</script>

<style scoped>
.dropdown-p-width {
  width: 0;
  min-width: 90%;
}
.min-w-36 {
  min-width: 9rem;
}
</style>